// GeoJSON bbox helpers — walk ANY geometry (Point/LineString/Polygon/MultiPolygon/
// GeometryCollection, wrapped in Feature/FeatureCollection or bare) down to a
// [west, south, east, north] bbox. No map dependency, so it's unit-testable.
// Why this exists: JD Ops boundaries come back as MultiPolygons, and a manifest
// boundary layer may be delivered inline OR fetched from a signed URL — the old
// MapWindow only handled a single inline Polygon (`coordinates[0]`), so a JD field
// never zoomed and never rendered. (Front-End.md §A1 / map auto-zoom.)

export type Bbox = [number, number, number, number];

function extend(acc: Bbox, lon: number, lat: number): void {
  if (lon < acc[0]) acc[0] = lon;
  if (lat < acc[1]) acc[1] = lat;
  if (lon > acc[2]) acc[2] = lon;
  if (lat > acc[3]) acc[3] = lat;
}

// A "position" is [lon, lat,...]; anything else is a nested array of positions.
function walk(coords: unknown, acc: Bbox): void {
  if (!Array.isArray(coords)) return;
  if (typeof coords[0] === "number" && typeof coords[1] === "number") {
    extend(acc, coords[0], coords[1]);
    return;
  }
  for (const c of coords) walk(c, acc);
}

function walkGeom(geom: GeoJSON.Geometry | null | undefined, acc: Bbox): void {
  if (!geom) return;
  if (geom.type === "GeometryCollection")
    geom.geometries.forEach((g) => walkGeom(g, acc));
  else walk((geom as { coordinates?: unknown }).coordinates, acc);
}

export function bboxOfGeoJSON(
  g: GeoJSON.GeoJSON | null | undefined,
): Bbox | null {
  if (!g || typeof g !== "object") return null;
  const acc: Bbox = [Infinity, Infinity, -Infinity, -Infinity];
  if (g.type === "FeatureCollection")
    g.features.forEach((f) => walkGeom(f.geometry, acc));
  else if (g.type === "Feature") walkGeom(g.geometry, acc);
  else walkGeom(g as GeoJSON.Geometry, acc);
  return acc.every((v) => Number.isFinite(v)) ? acc : null;
}

// A bbox usable for map.fitBounds: all-finite AND not a single degenerate point.
export function isFittableBbox(b: Bbox | null | undefined): b is Bbox {
  if (!b || b.length !== 4) return false;
  const [w, s, e, n] = b;
  return (
    [w, s, e, n].every((v) => typeof v === "number" && Number.isFinite(v)) &&
    (w !== e || s !== n)
  );
}

// (Bug: boundary invisible though map zoomed.
export type GeometryKind = "point" | "line" | "polygon" | null;

function classify(type: string): GeometryKind {
  if (type === "Point" || type === "MultiPoint") return "point";
  if (type === "LineString" || type === "MultiLineString") return "line";
  if (type === "Polygon" || type === "MultiPolygon") return "polygon";
  return null;
}

export function geometryKindOf(
  g: GeoJSON.GeoJSON | null | undefined,
): GeometryKind {
  if (!g || typeof g !== "object") return null;
  const first = (geom: GeoJSON.Geometry | null | undefined): GeometryKind => {
    if (!geom) return null;
    if (geom.type === "GeometryCollection") {
      for (const gg of geom.geometries) {
        const k = first(gg);
        if (k) return k;
      }
      return null;
    }
    return classify(geom.type);
  };
  if (g.type === "FeatureCollection") {
    for (const f of g.features) {
      const k = first(f.geometry);
      if (k) return k;
    }
    return null;
  }
  if (g.type === "Feature") return first(g.geometry);
  return first(g as GeoJSON.Geometry);
}
